import clsx from 'clsx';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, TextField, Tooltip, CircularProgress } from '@mui/material';
import InputMask from 'react-input-mask';
import { calculateCentimeterPixels } from 'utils';
import { MinAndMaxCreationSizeLimit } from 'enums';
import { makeStyles } from '@mui/styles';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { COLOR_PINK, COLOR_WHITE } from 'styles/variables/colors';

const Props = {
  busy: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['outlined', 'filled']),
  color: PropTypes.oneOf(['default', 'primary']),
  onSubmit: PropTypes.func.isRequired,
};

const DefaultProps = {
  variant: 'filled',
  color: 'default',
};

const useCustomStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(0, 0, 0, 1),
    minWidth: 130,
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLOR_PINK,
      borderWidth: '1px',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#A6A5A5',
    },
  },
  customLabel: {
    fontFamily: 'lato',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '20px',
    color: '#575757',
    textTransform: 'uppercase',
    marginLeft: theme.spacing(0.5),
  },
  customForm: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    margin: theme.spacing(2.5, 1, 0),
  },
  inputWrapper: {
    display: 'flex',
    gap: '24px',
  },
  select: {
    height: '48px',
    textAlign: 'left',
    '&.MuiButtonBase-root-MuiMenuItem-root.Mui-selected': {
      backgroundColor: COLOR_PINK,
      '&:hover': {
        backgroundColor: COLOR_PINK,
      },
    },
  },
  input: {
    height: '48px',
    width: '130px',
    '&.MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#A6A5A5',
        borderWidth: '1px !important',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: COLOR_PINK,
        borderWidth: '1px !important',
      },
    },
  },
  inputFocused: {
    borderColor: 'pink !important',
    borderWidth: '1px !important',
  },
  button: {
    height: '50px',
    width: '120px',
    color: COLOR_WHITE,
    backgroundColor: COLOR_PINK,
    borderWidth: '0px',
    borderRadius: '40px',
    marginBottom: '4px',
    textTransform: 'capitalize',
    '&:hover': {
      filter: 'brightness(0.9)',
      backgroundColor: COLOR_PINK,
      color: COLOR_WHITE,
      borderWidth: '0px',
    },
  },
  buttonDisabled: {
    opacity: 0.3,
    pointerEvents: 'none',
    '&.MuiButtonBase-root.MuiButton-root.Mui-disabled': {
      color: '#ffffff',
    },
  },
}));

export default function CustomSizeForm({ busy, disabled, onSubmit, variant, color }) {
  const classes = useCustomStyles();
  const [widthTooltipOpen, setWidthTooltipOpen] = useState(false);
  const [heightTooltipOpen, setHeightTooltipOpen] = useState(false);
  const [formData, setFormData] = useState({
    measurementUnit: 'px',
    width: '',
    height: '',
  });
  const isButtonDisabled = disabled || !formData.width || !formData.height;
  const [sizeUnit, setSizeUnit] = React.useState('px');

  function getInputRangeLabel() {
    if (formData.measurementUnit === 'px') {
      return `Min: ${MinAndMaxCreationSizeLimit.MIN} | Max: ${MinAndMaxCreationSizeLimit.MAX}`;
    }

    return 'Min: 4 | Max: 88';
  }

  function handleMeasurementUnitInputChange() {
    const newMeasurementUnit = formData.measurementUnit === 'px' ? 'cm' : 'px';
    const width = validateInputValue(formData.width, newMeasurementUnit);
    const height = validateInputValue(formData.height, newMeasurementUnit);

    setFormData({
      measurementUnit: newMeasurementUnit,
      width,
      height,
    });
  }

  function validateInputValue(value, measurementUnit) {
    if (measurementUnit === 'px') {
      value = parseInt(value) >= MinAndMaxCreationSizeLimit.MIN ? value : '';
    }

    if (measurementUnit === 'cm') {
      value = parseInt(value) >= 4 && parseInt(value) <= 88 ? value : '';
    }

    return value;
  }

  function handleInputBlur(fieldName) {
    const value = validateInputValue(formData[fieldName], formData.measurementUnit);

    if (value !== formData[fieldName]) {
      setFormData({ ...formData, [fieldName]: value });
    }

    setWidthTooltipOpen(false);
    setHeightTooltipOpen(false);
  }

  function handleSubmit(event) {
    event.preventDefault();

    let width = validateInputValue(formData.width, formData.measurementUnit);
    let height = validateInputValue(formData.height, formData.measurementUnit);

    if (width && height) {
      if (formData.measurementUnit === 'cm') {
        width = String(calculateCentimeterPixels(formData.width));
        height = String(calculateCentimeterPixels(formData.height));
      }

      onSubmit(Number(width), Number(height));
    } else {
      handleInputBlur('width');
      handleInputBlur('height');
    }
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit} className={classes.customForm}>
      <div className={classes.inputWrapper}>
        <Tooltip
          classes={{ tooltip: classes.tooltip }}
          open={widthTooltipOpen}
          title={getInputRangeLabel()}
          placement="top"
          disableFocusListener={true}
          disableHoverListener={true}
          disableTouchListener={true}
          PopperProps={{ disablePortal: true }}
          onClose={() => setWidthTooltipOpen(false)}
        >
          <FormHelperText className={classes.customLabel}>Width</FormHelperText>
          <TextField
            variant={variant}
            size="small"
            margin="dense"
            fullWidth={true}
            InputLabelProps={{
              classes: { root: classes.input },
            }}
            classes={{
              input: classes.bootstrapInput,
            }}
            InputProps={{
              classes: { root: classes.input },
              inputComponent: InputMask,
              inputProps: {
                mask: '9999',
                maskChar: '',
              },
              'aria-label': 'Width',
            }}
            value={formData.width}
            onChange={({ target: { value } }) => {
              if (value > MinAndMaxCreationSizeLimit.MAX_WIDTH) {
                value = MinAndMaxCreationSizeLimit.MAX_WIDTH;
              }
              setFormData({ ...formData, width: value });
            }}
            onFocus={() => setWidthTooltipOpen(true)}
            onBlur={() => handleInputBlur('width')}
          />
        </Tooltip>

        <Tooltip
          classes={{ tooltip: classes.tooltip }}
          open={heightTooltipOpen}
          title={getInputRangeLabel()}
          placement="bottom"
          disableFocusListener={true}
          disableHoverListener={true}
          disableTouchListener={true}
          PopperProps={{ disablePortal: true }}
          onClose={() => setHeightTooltipOpen(false)}
        >
          <FormHelperText className={classes.customLabel}>Height</FormHelperText>
          <TextField
            variant={variant}
            size="small"
            margin="dense"
            fullWidth={true}
            InputLabelProps={{ classes: { focused: classes.inputLabelFocused } }}
            InputProps={{
              classes: {
                root: classes.input,
              },
              inputComponent: InputMask,
              inputProps: {
                mask: '9999',
                maskChar: '',
              },
              'aria-label': 'Height',
            }}
            value={formData.height}
            onChange={({ target: { value } }) => {
              if (value > MinAndMaxCreationSizeLimit.MAX_HEIGHT) {
                value = MinAndMaxCreationSizeLimit.MAX_HEIGHT;
              }
              setFormData({ ...formData, height: value });
            }}
            onFocus={() => setHeightTooltipOpen(true)}
            onBlur={() => handleInputBlur('height')}
          />
        </Tooltip>

        <FormControl className={classes.formControl} sx={{ m: 1, minWidth: 120 }}>
          <FormHelperText className={classes.customLabel}>Unit</FormHelperText>
          <Select
            value={sizeUnit}
            onChange={(event) => {
              setSizeUnit(event.target.value);
              handleMeasurementUnitInputChange();
            }}
            displayEmpty
            inputProps={{ 'aria-label': 'Unit' }}
            sx={{ marginTop: '8px' }}
            className={classes.select}
          >
            <MenuItem value={'px'}>Pixels</MenuItem>
            <MenuItem value={'cm'}>Centimeter</MenuItem>
          </Select>
        </FormControl>
      </div>

      <Button
        classes={{
          root: clsx(classes.button, {
            [classes.defaultButton]: !isButtonDisabled && color === 'default',
            [classes.primaryButton]: !isButtonDisabled && color === 'primary',
          }),
          disabled: classes.buttonDisabled,
        }}
        variant="outlined"
        type="submit"
        disabled={isButtonDisabled}
      >
        {!busy ? 'Create' : <CircularProgress className={classes.progress} size={20} />}
      </Button>
    </form>
  );
}

CustomSizeForm.propTypes = Props;
CustomSizeForm.defaultProps = DefaultProps;
